import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileSpreadsheet, Loader2, Save, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

/**
 * Daily pricing report (xlsx) — user 2026-10-16. Pick investors (or a saved
 * group), pick a date, download one sheet: per investor cash, Deal Room and
 * every loan with a balance (calculated from cashflows), Total1, then a
 * Consolidated total per investor (layout of the user's RISE pricing file).
 * Backend: routers/investor_pricing.py (report.xlsx, report-groups).
 */
export default function PricingReportCard({ headers }) {
  const [investors, setInvestors] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [reportDate, setReportDate] = useState(yesterday());
  const [newName, setNewName] = useState('');
  const [cashRatePct, setCashRatePct] = useState('7.10');  // rate on the Cash rows, in %
  const [busy, setBusy] = useState(false);

  const loadGroups = async () => {
    const r = await axios.get(`${API_URL}/api/admin/investor-pricing/report-groups`, { headers });
    setGroups(r.data?.groups || []);
    return r.data?.groups || [];
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/investor-pricing/report/investors`, { headers })
      .then(r => setInvestors(r.data?.investors || []))
      .catch(() => toast.error('Could not load investors'));
    loadGroups().catch(() => toast.error('Could not load saved groups'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const group = groups.find(g => g.id === groupId);
  const pickGroup = (id) => {
    setGroupId(id);
    const g = groups.find(x => x.id === id);
    setSelected(new Set(g ? g.rp_codes : []));
    if (g && g.cash_rate != null) setCashRatePct((g.cash_rate * 100).toFixed(2));
  };
  const toggle = (code) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(code)) n.delete(code); else n.add(code);
    return n;
  });

  const q = search.trim().toLowerCase();
  // Inactive investors (left the book) are only listed when already ticked,
  // e.g. in an older saved group, so they can be unticked.
  const shown = useMemo(() => investors
    .filter(i => i.active !== false || selected.has(i.rp_code))
    .filter(i => !q || i.rp_code.toLowerCase().includes(q) || (i.name || '').toLowerCase().includes(q)),
    [investors, q, selected]);
  const rate = Number(cashRatePct) / 100;
  const rateOk = cashRatePct !== '' && Number.isFinite(rate) && rate >= 0 && rate < 1;
  const changed = group && (group.rp_codes.length !== selected.size || group.rp_codes.some(c => !selected.has(c))
    || (group.cash_rate == null ? rate !== 0.071 : Math.abs(group.cash_rate - rate) > 1e-9));
  // Report order = investor RP code order.
  const codes = investors.map(i => i.rp_code).filter(c => selected.has(c));

  const saveNew = async () => {
    if (!newName.trim()) { toast.error('Type a name for the group'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/admin/investor-pricing/report-groups`,
        { name: newName.trim(), rp_codes: codes, cash_rate: rateOk ? rate : null }, { headers });
      await loadGroups();
      setGroupId(r.data.id);
      setNewName('');
      toast.success(`Saved group "${r.data.name}"`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the group'); }
  };
  const updateGroup = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/investor-pricing/report-groups/${group.id}`,
        { name: group.name, rp_codes: codes, cash_rate: rateOk ? rate : null }, { headers });
      await loadGroups();
      toast.success(`Updated "${group.name}"`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update the group'); }
  };
  const deleteGroup = async () => {
    if (!window.confirm(`Delete the saved group "${group.name}"? The investors themselves are not affected.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/investor-pricing/report-groups/${group.id}`, { headers });
      setGroupId('');
      await loadGroups();
      toast.success('Group deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete the group'); }
  };

  const download = async () => {
    if (!codes.length) { toast.error('Pick at least one investor'); return; }
    if (!rateOk) { toast.error('Enter the cash rate as a percentage, e.g. 7.1'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/investor-pricing/report.xlsx`,
        { date: reportDate, rp_codes: codes, group_name: group?.name || null, cash_rate: rate },
        { headers, responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const name = (cd.match(/filename="([^"]+)"/) || [])[1] || `pricing_${reportDate}.xlsx`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Could not create the report';
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* not JSON */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="pricing-report-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" />Daily pricing report (xlsx)</CardTitle>
        <CardDescription>
          One table in your pricing layout: a Cash row per investor (approved general ledger, dated the pricing date, at
          the cash rate), Deal Room rows where held, every loan with a balance (calculated from its cashflows), Total1,
          then a Consolidated total per investor and Total2. The cash rate is saved with each group.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label htmlFor="pricing-report-date">Pricing date</Label>
            <input id="pricing-report-date" type="date" className="block mt-1 border rounded-md px-3 py-2 text-sm"
              value={reportDate} max={new Date().toISOString().slice(0, 10)}
              onChange={e => setReportDate(e.target.value)} data-testid="pricing-report-date" />
          </div>
          <div>
            <Label htmlFor="pricing-report-cashrate">Cash rate (%)</Label>
            <input id="pricing-report-cashrate" type="number" step="0.01" min="0" max="99.99"
              className="block mt-1 border rounded-md px-3 py-2 text-sm w-28"
              value={cashRatePct} onChange={e => setCashRatePct(e.target.value)} data-testid="pricing-report-cashrate" />
          </div>
          <div>
            <Label htmlFor="pricing-report-group">Saved group</Label>
            <select id="pricing-report-group" className="block mt-1 border rounded-md px-3 py-2 text-sm min-w-[220px]"
              value={groupId} onChange={e => pickGroup(e.target.value)} data-testid="pricing-report-group">
              <option value="">— none (pick investors below) —</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({g.rp_codes.length})</option>)}
            </select>
          </div>
          {group && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={!changed || !codes.length} onClick={updateGroup}
                title="Save the current ticks into this group" data-testid="pricing-report-update-group">
                <Save className="h-4 w-4 mr-1" />Update group
              </Button>
              <Button variant="outline" size="sm" onClick={deleteGroup} className="text-red-700 border-red-300"
                data-testid="pricing-report-delete-group">
                <Trash2 className="h-4 w-4 mr-1" />Delete group
              </Button>
            </div>
          )}
          <Button onClick={download} disabled={busy || !codes.length} data-testid="pricing-report-download">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
            Download xlsx ({codes.length} investor{codes.length === 1 ? '' : 's'})
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Find investor: RP code or name"
            className="h-9 w-72" data-testid="pricing-report-search" />
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set([...selected, ...shown.map(i => i.rp_code)]))}>Tick all shown</Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
          <span className="text-xs text-gray-500">{selected.size} selected</span>
        </div>
        <div className="border rounded-md max-h-72 overflow-y-auto divide-y" data-testid="pricing-report-investors">
          {shown.map(i => (
            <label key={i.rp_code} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
              <input type="checkbox" className="h-5 w-5" checked={selected.has(i.rp_code)} onChange={() => toggle(i.rp_code)} />
              <span className="font-mono w-16">{i.rp_code}</span>
              <span className="truncate">{i.name}</span>
              {i.active === false && <span className="text-[11px] text-amber-700 border border-amber-300 rounded px-1">inactive</span>}
            </label>
          ))}
          {shown.length === 0 && <div className="px-3 py-4 text-sm text-gray-500">No investors match.</div>}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label htmlFor="pricing-report-newname">Save the ticked investors as a new group</Label>
            <Input id="pricing-report-newname" value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Daily pricing — pension funds" className="h-9 w-72 mt-1" data-testid="pricing-report-newname" />
          </div>
          <Button variant="outline" onClick={saveNew} disabled={!codes.length || !newName.trim()} data-testid="pricing-report-save-group">
            <Save className="h-4 w-4 mr-1" />Save group
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
